import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../UserContext";
import { API_BASE_URL } from "../config/api";
import { completeLogin } from "../lib/session";
import "../pages/Login.css";

/**
 * Where Microsoft sign-in lands.
 *
 * The backend could not simply return the login JSON: the callback is a GET the
 * browser followed a redirect into, not an XHR this app made, so the user would
 * be staring at raw JSON. Nor could the JWT ride in the URL, URLs reach browser
 * history, Referer headers, and proxy logs. Instead the backend parks the
 * finished login body under a single-use, 60-second code and sends only that
 * code here; this page trades it for the real body.
 */
const SsoComplete = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useContext(UserContext);

  // The handoff code is single-use, so a second redemption legitimately fails.
  // React 18 StrictMode mounts effects twice in development, which would burn
  // the code on the first mount and show an error on the second.
  const ran = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    document.body.classList.add("nx-onboard-body");
    return () => document.body.classList.remove("nx-onboard-body");
  }, []);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const handoff = params.get("handoff");
    if (!handoff) {
      navigate("/auth/sso/error?reason=invalid_request", { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/sso/session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ handoff }),
        });
        if (!res.ok) throw new Error("handoff rejected");

        const data = await res.json();
        const next = await completeLogin(data, login);
        navigate(next, { replace: true });
      } catch {
        setFailed(true);
        navigate("/auth/sso/error?reason=handoff_failed", { replace: true });
      }
    })();
  }, [params, navigate, login]);

  return (
    <div className="nx-login-page">
      <div className="nx-content">
        <div className="nx-login-card nx-sso-status">
          <div className="nx-card-header">
            <img
              src="/images/NexOpta-Logo.png"
              alt="Nexopta"
              className="nx-card-logo"
            />
          </div>
          {!failed && (
            <p className="nx-sso-status-text" role="status">
              Signing you in&hellip;
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SsoComplete;
